import React, { useState, useEffect } from 'react';
import { 
  Star, 
  ThumbsUp, 
  ThumbsDown, 
  Send, 
  Heart,
  Lightbulb,
  Bug,
  MessageSquare,
  CheckCircle
} from 'lucide-react';
import { feedbackApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BetaFeedback = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    overall_rating: 0,
    ease_of_use: 0,
    feature_completeness: 0,
    would_recommend: null,
    favorite_feature: '',
    missing_feature: '',
    bugs_encountered: '',
    additional_comments: ''
  });

  useEffect(() => {
    loadExistingFeedback();
  }, []);

  const loadExistingFeedback = async () => {
    try {
      const res = await feedbackApi.getMyFeedback();
      if (res.data) {
        setFormData(res.data);
        setSubmitted(true);
      }
    } catch (error) {
      // No existing feedback
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.overall_rating === 0) {
      toast.error('Please provide an overall rating');
      return;
    }
    if (formData.ease_of_use === 0) {
      toast.error('Please rate ease of use');
      return;
    }
    if (formData.feature_completeness === 0) {
      toast.error('Please rate feature completeness');
      return;
    }
    if (formData.would_recommend === null) {
      toast.error('Please indicate if you would recommend the app');
      return;
    }

    setSubmitting(true);
    try {
      await feedbackApi.submitBetaFeedback(formData);
      toast.success('Thank you for your feedback!');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="space-y-2">
      <Label className="text-slate-700 dark:text-slate-300">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= value
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-slate-300 dark:text-slate-600'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#26C6DA]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-[#0F0F1A] dark:to-[#1A1A2E] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#26C6DA] to-[#8B5CF6] mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
            Beta Feedback
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Your feedback helps us build a better product
          </p>
        </div>

        {submitted ? (
          <div className="bg-white dark:bg-[#111827] rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-[#1F2937] text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
              Thank You!
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Your feedback has been submitted. We truly appreciate you taking the time to help us improve.
            </p>
            <Button
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="rounded-full"
            >
              Update Feedback
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white dark:bg-[#111827] rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-[#1F2937] space-y-8">
            {/* Star Ratings */}
            <div className="grid gap-6">
              <StarRating
                label="Overall Experience"
                value={formData.overall_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, overall_rating: v }))}
              />
              <StarRating
                label="Ease of Use"
                value={formData.ease_of_use}
                onChange={(v) => setFormData(prev => ({ ...prev, ease_of_use: v }))}
              />
              <StarRating
                label="Feature Completeness"
                value={formData.feature_completeness}
                onChange={(v) => setFormData(prev => ({ ...prev, feature_completeness: v }))}
              />
            </div>

            {/* Would Recommend */}
            <div className="space-y-3">
              <Label className="text-slate-700 dark:text-slate-300">
                Would you recommend Strategic Architect to others?
              </Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, would_recommend: true }))}
                  className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    formData.would_recommend === true
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-600'
                      : 'border-slate-200 dark:border-slate-700 hover:border-green-300'
                  }`}
                >
                  <ThumbsUp className="w-6 h-6" />
                  <span className="font-medium">Yes!</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, would_recommend: false }))}
                  className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    formData.would_recommend === false
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-600'
                      : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
                  }`}
                >
                  <ThumbsDown className="w-6 h-6" />
                  <span className="font-medium">Not yet</span>
                </button>
              </div>
            </div>

            {/* Text Feedback */}
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Heart className="w-4 h-4 text-pink-500" />
                  What's your favorite feature?
                </Label>
                <Textarea
                  value={formData.favorite_feature}
                  onChange={(e) => setFormData(prev => ({ ...prev, favorite_feature: e.target.value }))}
                  placeholder="Tell us what you love about the app..."
                  className="min-h-[80px] bg-slate-50 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  What feature is missing that you'd like to see?
                </Label>
                <Textarea
                  value={formData.missing_feature}
                  onChange={(e) => setFormData(prev => ({ ...prev, missing_feature: e.target.value }))}
                  placeholder="Any features you wish we had..."
                  className="min-h-[80px] bg-slate-50 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Bug className="w-4 h-4 text-red-500" />
                  Did you encounter any bugs or issues?
                </Label>
                <Textarea
                  value={formData.bugs_encountered}
                  onChange={(e) => setFormData(prev => ({ ...prev, bugs_encountered: e.target.value }))}
                  placeholder="Describe any problems you experienced..."
                  className="min-h-[80px] bg-slate-50 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <MessageSquare className="w-4 h-4 text-[#26C6DA]" />
                  Any other comments or suggestions?
                </Label>
                <Textarea
                  value={formData.additional_comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_comments: e.target.value }))}
                  placeholder="Anything else you'd like to share..."
                  className="min-h-[100px] bg-slate-50 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-[#0F5156] to-[#1a6b71] hover:from-[#0d4347] hover:to-[#155a5f] text-white rounded-full py-6 text-lg font-semibold"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Submit Feedback
                </span>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BetaFeedback;
